import * as React from 'react';
import * as _ from 'lodash';
import * as styles from '../support.module.scss';
import useSupport from '../../../hooks/useSupport';
import { AppContext } from '../../../shared/app-context';
import { SupportContext } from '../../../shared/support-context';
import MaterialIcon from '@material/react-material-icon';
import PageCount from './page-count';
import PagesDisplay from './pages-display';
import OutlinedInput from '../../common/outlined-input';

/**
 * SupportPagination
 * @function SupportPagination - Componenet to render pagination on the support page
 */
const SupportPagination = () => {
  const { fetchSupportTickets } = useSupport();
  const { state } = React.useContext(AppContext);
  const {
    selectedFilters,
    orderColumn,
    orderDirection,
    searchQuery,
    pageSize,
    totalTickets,
    currentPage
  } = React.useContext(SupportContext);
  const [inputPage, setInputPage] = React.useState<number | null>(null);

  const totalPages = Math.ceil(totalTickets / pageSize);
  const selectedPage = Math.ceil((currentPage + 1) / pageSize);
  const allPages = _.range(1, totalPages + 1);

  /**
   * handlePageChange
   * @function handlePageChange - Fetches the support tickets after the user selects the number page
   * @param newPage Selects which page will be requested
   */
  const handlePageChange = (newPage: number) => {
    if (newPage > totalPages) {
      newPage = totalPages;
    } else if (newPage < 1) {
      newPage = 1;
    }
    fetchSupportTickets(state.clientId, {
      ...selectedFilters,
      searchTerm: searchQuery,
      count: pageSize,
      startAt: (newPage - 1) * pageSize,
      orderColumn,
      orderDirection
    });
  };

  // Clears out the number in input if the selectedPage is changed outside the input
  React.useEffect(() => {
    if (selectedPage !== inputPage) {
      setInputPage(null);
    }
  }, [selectedPage]);

  return (
    <div className={styles.paginationContainer}>
      <div className={styles.pageSelection}>
        <span>Show: </span>
        <PageCount count={15} />
        <PageCount count={30} />
        <PageCount count={60} />
      </div>
      <div className={styles.pageNumbers}>
        {selectedPage !== 1 ? (
          <MaterialIcon icon="keyboard_arrow_left" onClick={() => handlePageChange(selectedPage - 1)} />
        ) : (
          <div />
        )}
        <PagesDisplay
          totalPages={totalPages}
          allPages={allPages}
          selectedPage={selectedPage}
          activeStyles={styles.activePage}
          handlePageChange={(page: number) => handlePageChange(page)}
        />
        {selectedPage !== totalPages && totalPages > 0 ? (
          <MaterialIcon icon="keyboard_arrow_right" onClick={() => handlePageChange(selectedPage + 1)} />
        ) : (
          <div />
        )}
      </div>
      <div className={styles.pageSelection}>
        <span>Go To Page:</span>
        <form
          onSubmit={event => {
            event.preventDefault();
            handlePageChange(inputPage);
          }}
        >
          <OutlinedInput
            id="outlined-page-input"
            margin="dense"
            variant="outlined"
            type="number"
            onChange={event => setInputPage(Number(event.target.value))}
            value={inputPage === 0 ? '0' : inputPage ? inputPage.toString().replace(/^0+/, '') : ''}
            tabIndex={1}
          />
        </form>
      </div>
    </div>
  );
};

export default SupportPagination;
